import { useState, type FormEvent } from "react";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import {
  Download, Check, Sparkles, FileText, Smartphone, Printer,
  Zap, ArrowRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { usePageView, trackConversion } from "@/hooks/use-page-view";
import { getPublicProduct, claimEarlyAccess } from "@/lib/products.functions";
import triviaCover from "@/assets/products/trivia-pack-cover.jpg.asset.json";
import triviaSample from "@/assets/products/trivia-pack-sample.jpg.asset.json";

const SLUG = "ultimate-trivia-pack";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Route = createFileRoute("/shop/ultimate-trivia-pack")({
  head: () => ({
    meta: [
      { title: "Ultimate Trivia Pack - 100 wild science questions · Smartasaurus" },
      {
        name: "description",
        content:
          "100 surprising, conversation-starting trivia questions across Animals, Space, Human Body, and Weird Science. Instant PDF, mobile-friendly, printable.",
      },
      { property: "og:title", content: "Ultimate Trivia Pack · Smartasaurus" },
      {
        property: "og:description",
        content:
          "100 questions you'll actually want to know the answer to. Instant PDF. Free during early access.",
      },
      { property: "og:url", content: "https://smartasaurus.com/shop/ultimate-trivia-pack" },
      { property: "og:type", content: "product" },
      {
        property: "og:image",
        content:
          "https://smartasaurus.com/__l5e/assets-v1/e594e13a-3ad6-4bca-8a4b-acdc37a7ae1a/trivia-pack-cover.jpg",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Ultimate Trivia Pack · Smartasaurus" },
      {
        name: "twitter:description",
        content: "100 questions you'll actually want to know the answer to.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://smartasaurus.com/shop/ultimate-trivia-pack" },
    ],
  }),
  loader: async ({ context }) => {
    const product = await context.queryClient.fetchQuery({
      queryKey: ["product", SLUG],
      queryFn: () => getPublicProduct({ data: { slug: SLUG } }),
    });
    if (!product) throw notFound();
    return { product };
  },
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl uppercase">Product not found</h1>
        <p className="mt-3 text-muted-foreground">
          That product isn't available right now.
        </p>
        <Link to="/shop" className="mt-6 inline-block rounded-full bg-primary px-6 py-3 text-xs font-bold uppercase tracking-widest text-primary-foreground">
          Back to shop
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl uppercase">Something went wrong</h1>
        <p className="mt-3 text-muted-foreground">Try refreshing the page.</p>
      </main>
      <SiteFooter />
    </div>
  ),
  component: ProductPage,
});

function ProductPage() {
  usePageView("product:ultimate-trivia-pack");
  const { product } = Route.useLoaderData();

  const meta = product?.metadata ?? {};
  const coverUrl = triviaCover.url;
  const sampleUrl = triviaSample.url;
  const categories: string[] = meta.categories ?? [
    "Animals", "Space", "Human Body", "Weird Science",
  ];
  const questionCount = meta.question_count ?? 100;
  const isEarlyAccess = meta.status === "early_access";

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="pt-6 pb-20">
        <div className="mx-auto max-w-6xl px-4 md:px-8">
          <Breadcrumbs
            items={[
              { label: "Shop", to: "/shop" },
              { label: product?.name ?? "Ultimate Trivia Pack", current: true },
            ]}
          />


          <div className="mt-6 grid gap-10 md:grid-cols-2 md:gap-14">
            {/* Cover */}
            <div className="order-1 md:order-1">
              <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-[0_20px_80px_-30px_hsl(var(--primary)/0.4)]">
                {coverUrl ? (
                  <img
                    src={coverUrl}
                    alt={`${product?.name ?? "Ultimate Trivia Pack"} cover`}
                    width={744}
                    height={1052}
                    className="aspect-[744/1052] h-auto w-full object-cover"
                  />
                ) : null}
              </div>
              {sampleUrl ? (
                <div className="mt-4 overflow-hidden rounded-2xl border border-border bg-card">
                  <div className="border-b border-border bg-muted/40 px-4 py-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                    Sample page
                  </div>
                  <img
                    src={sampleUrl}
                    alt="Inside preview"
                    loading="lazy"
                    width={744}
                    height={1052}
                    className="h-auto w-full"
                  />
                </div>
              ) : null}
            </div>

            {/* Early-access panel */}
            <div className="order-2 md:order-2">
              {isEarlyAccess ? (
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-primary">
                  <Sparkles className="h-3 w-3" />
                  Early Access · Free for first readers
                </div>
              ) : null}
              <h1 className="font-display text-3xl uppercase leading-tight md:text-5xl">
                {product?.name ?? "Ultimate Trivia Pack"}
              </h1>
              <p className="mt-3 text-lg font-medium text-foreground/90">
                {questionCount} questions you'll actually want to know the answer to.
              </p>
              <p className="mt-3 text-sm text-muted-foreground md:text-base">
                {product?.description ??
                  "Animals, Space, Human Body, and Weird Science. Premium PDF, mobile-friendly, printable."}
              </p>

              {/* Category strip */}
              <div className="mt-5 flex flex-wrap gap-2">
                {categories.map((c) => (
                  <span
                    key={c}
                    className="rounded-full border border-border bg-card px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-foreground/80"
                  >
                    {c}
                  </span>
                ))}
              </div>

              {/* Format pills */}
              <div className="mt-5 flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1">
                  <FileText className="h-3 w-3" /> 50-page PDF
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1">
                  <Smartphone className="h-3 w-3" /> Mobile-friendly
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1">
                  <Printer className="h-3 w-3" /> Printable
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1">
                  <Zap className="h-3 w-3" /> Instant download
                </span>
              </div>

              {/* What you get */}
              <ul className="mt-6 space-y-2 text-sm text-foreground/90">
                {[
                  `${questionCount} multiple-choice questions across four categories`,
                  "Complete answer key with the explanation behind every answer",
                  "Printable score sheet for up to six players",
                  "Sudden-death tiebreaker round for when a game ends level",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {/* CTA box */}
              <div id="claim-cta" className="mt-8 rounded-2xl border-2 border-primary/30 bg-primary/5 p-5 md:p-6">
                <ClaimForm />
                <p className="mt-3 text-[11px] text-muted-foreground">
                  No spam. One email when the next pack drops. Unsubscribe any time.
                </p>
              </div>

              <div className="mt-6 text-xs text-muted-foreground">
                Published by{" "}
                <Link to="/about" className="font-semibold text-foreground underline-offset-4 hover:underline">
                  Smartasaurus
                </Link>
                . For personal use. Please share the link instead of the file.
              </div>
            </div>
          </div>

          {/* What's inside */}
          <section className="mt-16 border-t border-border pt-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· What's inside</div>
            <h2 className="mt-2 font-display text-3xl uppercase leading-tight md:text-4xl">
              4 categories. 100 questions. <span className="text-gradient-curiosity">Zero filler.</span>
            </h2>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { name: "Animals", emoji: "🦑", note: "Octopus brains, ant cities, animals that defy biology." },
                { name: "Space", emoji: "🪐", note: "Black holes, time dilation, the most extreme objects in the universe." },
                { name: "Human Body", emoji: "🧠", note: "Your gut, your brain, the cells that rebuild you weekly." },
                { name: "Weird Science", emoji: "⚡", note: "Quantum oddities, lab accidents, the science teachers skip." },
              ].map((c) => (
                <div key={c.name} className="rounded-2xl border border-border bg-card p-4 transition hover:border-primary/40">
                  <div className="text-2xl">{c.emoji}</div>
                  <div className="mt-2 font-display text-lg uppercase leading-tight">{c.name}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{c.note}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-16 border-t border-border pt-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· FAQ</div>
            <h2 className="mt-2 font-display text-3xl uppercase leading-tight md:text-4xl">Quick answers</h2>
            <div className="mt-6 grid gap-2 md:grid-cols-2">
              {[
                { q: "Is it really free?", a: "Yes - during early access, drop your email and you get the PDF immediately. No card, no trial." },
                { q: "What's the format?", a: "A 50-page PDF with a score sheet, category rounds, a full answer key and a tiebreaker. Works on phones, tablets and laptops, and prints cleanly on A4." },
                { q: "Who is it for?", a: "Anyone who loves a 'wait, what?' moment. Solo play, family dinners, road trips, group chats." },
                { q: "Can I share it?", a: "Please share the link, not the file - it keeps the project alive and lets others get the next drop too." },
              ].map((f) => (
                <details key={f.q} className="group rounded-2xl border border-border bg-card p-4 open:border-primary/40">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-display text-sm uppercase leading-tight md:text-base">
                    <span>{f.q}</span>
                    <span className="text-primary transition group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-2 text-sm text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Related */}
          <section className="mt-16 border-t border-border pt-12">
            <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Keep exploring</div>
            <h2 className="mt-2 font-display text-3xl uppercase leading-tight md:text-4xl">More from Smartasaurus</h2>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-3">
              {[
                { to: "/play" as const, label: "Play", note: "Test what you know. Earn XP." },
                { to: "/collections" as const, label: "Collections", note: "Themed deep-dives, episode by episode." },
                { to: "/discoveries" as const, label: "Discoveries", note: "The full curiosity feed." },
              ].map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="group rounded-2xl border border-border bg-card p-5 transition hover:border-primary/40"
                >
                  <div className="font-display text-lg uppercase leading-tight group-hover:text-primary">{r.label}</div>
                  <p className="mt-1 text-xs text-muted-foreground">{r.note}</p>
                  <div className="mt-3 text-[10px] font-bold uppercase tracking-widest text-primary">Open →</div>
                </Link>
              ))}
            </div>
          </section>
        </div>

        {/* Sticky mobile CTA */}
        <div
          className="fixed inset-x-0 z-40 md:hidden"
          style={{ bottom: "calc(env(safe-area-inset-bottom) + 8px)" }}
        >
          <div className="mx-3 rounded-full border border-primary/40 bg-background/95 p-1.5 shadow-[0_10px_40px_-10px_hsl(var(--primary)/0.5)] backdrop-blur">
            <a
              href="#claim-cta"
              onClick={() => trackConversion("product_sticky_cta_click")}
              className="flex items-center justify-center gap-2 rounded-full bg-primary px-5 py-3 text-xs font-bold uppercase tracking-widest text-primary-foreground"
            >
              <Download className="h-4 w-4" /> Get the PDF - free
            </a>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function ClaimForm() {
  const claim = useServerFn(claimEarlyAccess);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done" | "error">("idle");
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setStatus("error");
      setMsg("Please enter a valid email address.");
      return;
    }
    setStatus("loading"); setMsg(null);
    trackConversion("product_cta_click");
    try {
      const res = await claim({ data: { email: trimmed, slug: SLUG } });
      if (!res.ok) {
        setStatus("error"); setMsg(res.reason);
        return;
      }
      setStatus("done");
      setDownloadUrl(res.download_url);
      setMsg(
        res.duplicate
          ? "Welcome back - here's your download."
          : "You're in. Your download is ready below."
      );
      trackConversion("product_download_unlocked");
      toast.success("Download ready");
    } catch (err) {
      setStatus("error");
      setMsg((err as Error)?.message ?? "Something went wrong. Try again.");
      toast.error("Could not unlock download");
    }
  };

  if (status === "done" && downloadUrl) {
    return (
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
          · Your download
        </div>
        <h3 className="mt-1 font-display text-xl uppercase">{msg}</h3>
        <a
          href={downloadUrl}
          download
          onClick={() => trackConversion("product_download_clicked")}
          className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-widest text-primary-foreground transition hover:brightness-110"
        >
          <Download className="h-4 w-4" /> Download the PDF
        </a>
        <p className="mt-3 text-[11px] text-muted-foreground">
          The link works on any device. If you lose it, just submit your email again.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-3" noValidate>
      <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">
        · Get the PDF - free during early access
      </div>
      <label htmlFor="email" className="sr-only">Email</label>
      <input
        id="email"
        type="email"
        required
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (status !== "idle") setStatus("idle"); }}
        placeholder="your@email.com"
        disabled={status === "loading"}
        aria-invalid={status === "error"}
        className="rounded-full border border-border bg-background px-5 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none disabled:opacity-60"
      />
      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-6 py-4 text-sm font-bold uppercase tracking-widest text-primary-foreground transition hover:brightness-110 disabled:opacity-60"
      >
        {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
        {status === "loading" ? "Preparing…" : "Send me the PDF"}
      </button>
      {msg ? (
        <p
          role={status === "error" ? "alert" : "status"}
          className={`text-xs ${status === "error" ? "text-destructive" : "text-muted-foreground"}`}
        >
          {msg}
        </p>
      ) : null}
    </form>
  );
}
